import { notification } from "antd";
import { ExecuteReportValues } from "shared/types";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ReportFormatEnum,
  ReportItem,
  useExecuteReportMutation,
  useGetReportDataMutation,
} from "shared/api";
import { downloadJsonFile, handleRequestError } from "shared/helpers";
import {
  getProductCardRequestParams,
  isXlsOnlyReport,
  isXlsxPreviewOnlyReport,
} from "shared/helpers/reports";

interface UseExecuteReportsRequestsProps {
  values: ExecuteReportValues;
  selectedReport?: ReportItem;
}

export const useExecuteReportsRequests = ({
  values,
  selectedReport,
}: UseExecuteReportsRequestsProps) => {
  const { t } = useTranslation("", { keyPrefix: "executeReports" });

  const [api, contextHolder] = notification.useNotification();

  const [executeReport, { isLoading: isLoadingReportExport }] =
    useExecuteReportMutation();

  const [getReportData, { isLoading: isLoadingReportData }] =
    useGetReportDataMutation();

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const clearPdfUrl = () => setPdfUrl(null);

  const handleExecuteReport = async () => {
    const productCardParams = getProductCardRequestParams(
      values.product_card_list_id
    );
    const orderProductionDatetimeStart =
      values.order_production_datetime_start ||
      values.production_datetime_start;
    const orderProductionDatetimeEnd =
      values.order_production_datetime_end || values.production_datetime_end;

    // The preview (previsualization) for these reports is always rendered as
    // XLSX, regardless of the format selected for the downloaded report.
    const previewFormat = isXlsxPreviewOnlyReport(selectedReport)
      ? ReportFormatEnum.XLSX
      : values.report_format;

    try {
      const response = await executeReport({
        id: values.id!,
        material_id: values.material_id,
        material_item_list_id: values.material_item_list_id,
        input_material_id: values.input_material_id,
        material_item_id: values.material_item_id,
        order_id: values.order_id,
        order_production_datetime_end: orderProductionDatetimeEnd,
        order_production_datetime_start: orderProductionDatetimeStart,
        production_datetime_start: values.production_datetime_start,
        production_datetime_end: values.production_datetime_end,
        // order_wanted_delivery_datetime_end:
        //   values.order_wanted_delivery_datetime_end,
        // order_wanted_delivery_datetime_start:
        //   values.order_wanted_delivery_datetime_start,
        packaging_id: values.packaging_id,
        report_format: previewFormat,
        quality_result_id: values.quality_result_id,
        packaging_ids: values.packaging_ids,
        drigging_rigg_datetime_start: values.drigging_rigg_datetime_start,
        drigging_rigg_datetime_end: values.drigging_rigg_datetime_end,
        drigging_rigg_field: values.drigging_rigg_field,
        drigging_rigg_n_points: values.drigging_rigg_n_points,
        company_id: values.company_id,
        company_ids: values.company_ids,
        material_category_id_list: values.material_category_id_list,
        order_ids: values.order_ids,
        material_category_id: values.material_category_id,
        dynamic_tracker_fields: values.dynamic_tracker_fields,
        timestamp_start: values.timestamp_start,
        timestamp_end: values.timestamp_end,
        ...productCardParams,
        quality_result_material_item_timestamp_end:
          values.quality_result_material_item_timestamp_end,
        quality_result_material_item_timestamp_start:
          values.quality_result_material_item_timestamp_start,
        material_item_status: values.material_item_status,
        material_item_situation: values.material_item_situation,
      }).unwrap();

      if (response !== null && response !== undefined) {
        const isExcel = previewFormat === "xlsx" || previewFormat === "xls";

        // These reports are always emitted as OLE2 (real XLS) by the backend,
        // whatever format the request asked for, so they need the legacy MIME.
        let mimeType = "application/pdf";

        if (isXlsOnlyReport(selectedReport)) {
          mimeType = "application/vnd.ms-excel";
        } else if (isExcel) {
          mimeType =
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        }

        const file = new Blob([response], { type: mimeType });
        const fileURL = URL.createObjectURL(file);

        setPdfUrl(fileURL);
        // window.open(fileURL);
      }

      api.success({
        message: t("generatedReport"),
      });
    } catch (error) {
      api.error({
        message: t("error"),
        description: handleRequestError(error, t("error")),
      });
    }
  };

  const handleGetReportData = async () => {
    const productCardParams = getProductCardRequestParams(
      values.product_card_list_id
    );
    const orderProductionDatetimeStart =
      values.order_production_datetime_start ||
      values.production_datetime_start;
    const orderProductionDatetimeEnd =
      values.order_production_datetime_end || values.production_datetime_end;

    try {
      const response = await getReportData({
        id: values.id!,
        material_id: values.material_id,
        material_item_list_id: values.material_item_list_id,
        input_material_id: values.input_material_id,
        material_item_id: values.material_item_id,
        order_id: values.order_id,
        order_production_datetime_end: orderProductionDatetimeEnd,
        order_production_datetime_start: orderProductionDatetimeStart,
        production_datetime_start: values.production_datetime_start,
        production_datetime_end: values.production_datetime_end,
        // order_wanted_delivery_datetime_end:
        //   values.order_wanted_delivery_datetime_end,
        // order_wanted_delivery_datetime_start:
        //   values.order_wanted_delivery_datetime_start,
        packaging_id: values.packaging_id,
        report_format: values.report_format,
        quality_result_id: values.quality_result_id,
        packaging_ids: values.packaging_ids,
        drigging_rigg_datetime_start: values.drigging_rigg_datetime_start,
        drigging_rigg_datetime_end: values.drigging_rigg_datetime_end,
        drigging_rigg_field: values.drigging_rigg_field,
        drigging_rigg_n_points: values.drigging_rigg_n_points,
        company_id: values.company_id,
        company_ids: values.company_ids,
        material_category_id_list: values.material_category_id_list,
        order_ids: values.order_ids,
        material_category_id: values.material_category_id,
        dynamic_tracker_fields: values.dynamic_tracker_fields,
        timestamp_start: values.timestamp_start,
        timestamp_end: values.timestamp_end,
        ...productCardParams,
        quality_result_material_item_timestamp_end:
          values.quality_result_material_item_timestamp_end,
        quality_result_material_item_timestamp_start:
          values.quality_result_material_item_timestamp_start,
        material_item_status: values.material_item_status,
        material_item_situation: values.material_item_situation,
      }).unwrap();

      downloadJsonFile(
        response,
        t("reportData", { reportName: selectedReport?.name })
      );

      api.success({
        message: t("generatedReport"),
      });
    } catch (error) {
      api.error({
        message: t("error"),
        description: handleRequestError(error, t("error")),
      });
    }
  };

  return {
    contextHolder,
    executeReports: {
      handleExecuteReport,
      isLoadingReportExport,
    },
    getReportData: {
      handleGetReportData,
      isLoadingReportData,
    },
    pdfUrl,
    clearPdfUrl,
  };
};
